/**
 * THE SCRIPT ORDER — the graph, walked into the order a caller hears it.
 *
 * The canvas draws a flow as a shape; the SCRIPT view reads it top to bottom as the call
 * actually runs. This module is the one place that turns `{ nodes, edges }` into that ordered
 * list, and it does it the same way the compiler and `tidy()` do: follow the single wire out
 * of every step, and at a branch take each outcome in turn, indented one level, before
 * carrying on. A rejoin is visited once (the honest simplification — the compiler flattens
 * too), and anything the walk never reaches comes back as `orphans` so the editor can still
 * show it, let you wire it in, or let you delete it. Nothing here mutates the graph.
 *
 * `describeStep` is the other half: the sentence a row shows, derived from the SAME node data
 * the cards and the read-through use, so the three never drift into describing different flows.
 */
import { casesOf, nodeById, outgoing } from './graph.ts';
import type { NdGraph, NdNode } from './graph.ts';
import { EMPTY_LINE, MESSAGE_CATEGORIES, NODE_SPECS, toolLabel } from './model.ts';
import type { NdKind, NdTone } from './model.ts';
import type { IconName } from '../iconNames.ts';

export type ScriptRow =
  | { type: 'step'; key: string; node: NdNode; depth: number }
  /** The "If <value> →" header that opens a branch outcome. */
  | { type: 'case'; key: string; branchId: string; value: string; depth: number; wired: boolean };

export type ScriptOrder = {
  rows: ScriptRow[];
  /** Nodes the walk never reached — loose, not yet wired into the call. */
  orphans: NdNode[];
};

/** The entry the call starts at: the declared entry, else whatever nothing points into. */
function startNode(graph: NdGraph): NdNode | undefined {
  const entry = graph.nodes.find((n) => n.type === 'entry');
  if (entry) return entry;
  const targets = new Set(graph.edges.map((e) => e.target));
  return graph.nodes.find((n) => !targets.has(n.id)) ?? graph.nodes[0];
}

export function orderRows(graph: NdGraph): ScriptOrder {
  const rows: ScriptRow[] = [];
  const visited = new Set<string>();

  const walk = (id: string, depth: number): void => {
    const node = nodeById(graph, id);
    if (!node || visited.has(id)) return;
    visited.add(id);
    rows.push({ type: 'step', key: id, node, depth });

    const outs = outgoing(graph, id);
    if (node.type === 'branch') {
      for (const value of casesOf(node)) {
        const edge = outs.find((e) => (e.sourceHandle ?? null) === value);
        rows.push({
          type: 'case',
          key: `${id}::${value}`,
          branchId: id,
          value,
          depth: depth + 1,
          wired: Boolean(edge),
        });
        if (edge) walk(edge.target, depth + 1);
      }
      return;
    }

    const next = outs[0];
    if (next) walk(next.target, depth);
  };

  const start = startNode(graph);
  if (start) walk(start.id, 0);

  const orphans = graph.nodes.filter((n) => !visited.has(n.id));
  return { rows, orphans };
}

// ------------------------------------------------------------------- the sentence per step

export type StepFace = {
  eyebrow: string;
  tone: NdTone;
  icon: IconName;
  /** The main line, plain narration. */
  body: string;
  /** Rendered as a quotation when present — the words the caller hears, verbatim. */
  quote?: string;
  /** A quieter second line. */
  sub?: string;
  /** A doctrine promise, rendered as the locked idiom. */
  rule?: string;
  /** A small mono tag pinned to the right — the name an `ask` answer is remembered under. */
  tag?: string;
};

function str(node: NdNode, key: string): string {
  const value = node.data[key];
  return typeof value === 'string' ? value : '';
}

/**
 * What a script row says. Mirrors the node cards (`nodes.tsx`) and the read-through
 * (`readthrough.ts`) so an owner reads the same flow whichever panel they are looking at.
 */
export function describeStep(node: NdNode, situationLabel: (key: string) => string): StepFace {
  const kind = node.type as NdKind;
  const spec = NODE_SPECS[kind];
  const base: StepFace = { eyebrow: spec.eyebrow, tone: spec.tone, icon: spec.icon, body: '' };

  switch (kind) {
    case 'entry':
      return { ...base, body: 'A call comes in.' };

    case 'say': {
      const text = str(node, 'text').trim();
      return text ? { ...base, body: '', quote: text } : { ...base, body: EMPTY_LINE.say };
    }

    case 'ask': {
      const text = str(node, 'text').trim();
      const key = str(node, 'key').trim();
      return {
        ...base,
        body: text ? '' : EMPTY_LINE.ask,
        quote: text || undefined,
        sub: key ? undefined : 'The answer has no name yet, so nothing can branch on it.',
        tag: key || undefined,
      };
    }

    case 'branch': {
      const on = str(node, 'on').trim();
      return { ...base, body: on ? `Depending on “${on}”.` : EMPTY_LINE.branch };
    }

    case 'lookup': {
      const tool = str(node, 'tool').trim();
      return { ...base, body: tool ? toolLabel(tool) : EMPTY_LINE.lookup, rule: spec.rule };
    }

    case 'do': {
      const tool = str(node, 'tool').trim();
      return { ...base, body: tool ? toolLabel(tool) : EMPTY_LINE.do, rule: spec.rule };
    }

    case 'page': {
      const who = str(node, 'who').trim();
      return {
        ...base,
        body: 'The caller leaves a voicemail, then on-call is paged.',
        sub: who ? `Note on the page: ${who}` : undefined,
        rule: spec.rule,
      };
    }

    case 'message': {
      const category = str(node, 'category') || 'customer';
      const label = MESSAGE_CATEGORIES.find((c) => c.value === category)?.label ?? category;
      return { ...base, body: 'The caller leaves a message.', sub: `Filed as ${label.toLowerCase()}.` };
    }

    case 'arm': {
      const key = str(node, 'situation').trim();
      return {
        ...base,
        body: key ? situationLabel(key) : EMPTY_LINE.arm,
        sub: key ? 'Held until somebody releases it.' : undefined,
        rule: spec.rule,
      };
    }

    case 'end':
      return { ...base, body: 'The call ends.' };

    default:
      return { ...base, body: spec.blurb };
  }
}
